import React, { useCallback } from 'react'
import { useDispatch } from 'APP/Hooks/redux'
import { createNavigationContainerRef, NavigationContainer } from '@react-navigation/native'

// Config
import Config from 'APP/Config'

// Services & Helpers
import Analytics from 'APP/Services/Analytics'
import { createStatusBarStyleSetter } from 'APP/Nav/StatusBar'
import { ANALYTICS_SCREEN_BLACKLIST } from 'APP/Nav/Constants'
import { createScreenFocusTracker, getDdViewContext, routeNameToAnalyticsName } from 'APP/Nav/Utils'
import { startDdView, logger } from 'APP/Lib/Datadog'
import { userNavigationKeepAlive } from 'APP/Store/Brownfield/sagas'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import { useTheme, DefaultTheme } from 'styled-components/native'
import { ColourScheme } from 'APP/Helpers/AppearanceUtils'
import { RouteName, TypedRoute } from 'APP/Nav/types'
import { NavigationContainerProps } from '@react-navigation/core'
import type { DocumentTitleOptions, LinkingOptions } from '@react-navigation/native/src/types'
import type { HistoryState } from 'APP/Store/History/types'

export const navigationRef = createNavigationContainerRef()
export const setStatusBarStyle = createStatusBarStyleSetter()

const CHAT_ROUTES = new Set<RouteName>(['chatTab', 'conversation', 'newConversation'])

// Visible for testing
export const logChatNavigation = (currentRoute: RouteName, history: HistoryState) => {
  if (!CHAT_ROUTES.has(currentRoute)) return

  const { mattermostLongPollRunning, mmSocketNotReady } = history
  const mattermostSocketReady = !mmSocketNotReady
  logger.debug('[Nav] Chat Navigation', {
    currentRoute: currentRoute,
    mattermostLongPollRunning,
    mattermostSocketReady,
  })

  if (!mattermostLongPollRunning && !mattermostSocketReady) {
    logger.error('[Nav] Navigating to a chat route without Mattermost connection', {
      currentRoute,
      mattermostLongPollRunning,
      mattermostSocketReady,
    })
  }
}

// Visible for testing
export const onFocus = (route: TypedRoute | undefined) => {
  if (!route) return
  const currentRoute = route.name
  const shouldBeTracked = ANALYTICS_SCREEN_BLACKLIST.indexOf(currentRoute) === -1

  if (shouldBeTracked) {
    const analyticsName = routeNameToAnalyticsName(currentRoute)
    Analytics.trackScreen(analyticsName)
  }

  // TODO: restructure files to avoid circular import and require() method
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  logChatNavigation(currentRoute, require('APP/Store').default.getState().history)

  // Start tracking the view
  startDdView(route.key ?? currentRoute, currentRoute, getDdViewContext(route.params))
}

const screenFocusTracker = createScreenFocusTracker(navigationRef, {
  focus: onFocus,
})

const onStateChange: NonNullable<NavigationContainerProps['onStateChange']> = (_state) => {
  screenFocusTracker()

  /*

      TODO: Revisit the following when we upgrade expo.
      Update: (June 26 2023): We're on the expo version which supports statusBarStyle, so we can do it now

      Note:
      React-navigation supports statusbar style control out of box, using statusBarStyle prop:
      https://reactnavigation.org/docs/7.x/native-stack-navigator/#statusbarstyle

      However, it requires setting UIViewControllerBasedStatusBarAppearance to
      true in Info.plist, which causes a conflict with our current configuration.
      Looks like we need to update to latest RN.

      See also:
      https://github.com/react-navigation/react-navigation/issues/7916
      https://stackoverflow.com/questions/66489117/how-to-fix-rctstatusbarmanager-module-requires-that-the-uiviewcontrollerbasedst

      In the meanwhile, the following fills the gap by manually controlling it.
      Using __statusBarStyle to prevent the warning from popping on debugger.

    */

  const { __statusBarStyle } = (navigationRef?.getCurrentOptions() || {}) as {
    __statusBarStyle?: (typeof ColourScheme)[keyof typeof ColourScheme]
  }
  setStatusBarStyle(__statusBarStyle)
}

export const documentTitle = {
  formatter: () => Config.PRODUCT_NAME_WEB,
}

type NavProviderProps = NavigationContainerProps & {
  theme?: DefaultTheme
  linking?: LinkingOptions<ReactNavigation.RootParamList>
  fallback?: React.ReactNode
  documentTitle?: DocumentTitleOptions
  onReady?: () => void
  testID?: string
}

// TODO: Enable automatic view tracking and remove startDdView - https://docs.datadoghq.com/real_user_monitoring/mobile_and_tv_monitoring/react_native/setup/expo#automatic-tracking
export const NavProvider = (props: NavProviderProps) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const handleStateChange = useCallback<NonNullable<NavigationContainerProps['onStateChange']>>(
    (state) => {
      onStateChange(state)

      // When in an embedded context, emit user navigation keepalive events
      // The goal of these events is to let the host app know that there is embedded app user
      // activity. The host app can use these events to keep authentication sessions alive and
      // keep track of user navigation routes from the embedded app.
      if (isEmbeddedEnvironment() && navigationRef.isReady()) {
        const currentRoute = navigationRef.getCurrentRoute() as TypedRoute | undefined

        dispatch(userNavigationKeepAlive({ currentRoute }))
      }
    },
    [dispatch]
  )

  return (
    <NavigationContainer
      ref={navigationRef}
      onStateChange={handleStateChange}
      documentTitle={documentTitle}
      {...props}
      // @ts-expect-error: styled-components DefaultTheme is intentionally passed as NavigationContainer's theme but is structurally incompatible with @react-navigation/native's Theme type
      theme={theme}
    />
  )
}
